import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../rootReducer';
import { AgGridReact } from 'ag-grid-react';
import { Link } from 'react-router-dom';
import { ColDef } from 'ag-grid-community';
import { exportToCsv, handleExportClick } from '../service/csvExporter';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios, { AxiosResponse } from 'axios';
import logoInProgressColored from '../assets/icon-inprogress-colored.svg';
import logoFinishedColored from '../assets/icon-finished-colored.svg';
import logoDraftColored from '../assets/draft.svg';
import logoFbColored from '../assets/icon-facebook-colored.svg';
import logoInstaColored from '../assets/icon-instagram-colored.svg';
import logoGmbColored from '../assets/icon-google-colored.svg';
import logoInColored from '../assets/icon-linkedin-colored.svg';
import archive from '../assets/archive.svg';
import editOn from '../assets/editOn.svg';
import stat from '../assets/stat.svg';
import progPubli from '../assets/progPubli.svg';
import unselected from '../assets/unselected.svg';
import Pagination from '../pagination/Pagination';
import Spinner from '../spinner/Spinner';
import mediaUrl from '../service/mediaUrl';
import Filter from '../filter/filter';
import DateRangeComponent from '../daterangepicker/DateRangeComponent';
import format from 'date-fns/format';
import logoNF from '../assets/logo_news_factory.svg';
import visibility from '../assets/visibility.png';
import Modal from '../modal/Modal';
import AutoPubSwitch from './AutoPubSwitch';
import LogoSwitch from './LogoSwitch';
import downloadIcon from '../assets/download.svg';
import sortIcon from '../assets/sort.svg';
import dateIcon from '../assets/date.svg';
import { statusCellRenderer } from '../statusCellRenderer/statusCelleRenderer';
import { AddPublicationButton } from '../addPublicationButton/AddPublicationButton';
import { socialMediaCellRenderer } from '../socialMediaCellRenderer/socialMediaCellRenderer';
import { fetchPublicationsForCalendarRange } from './calendar/fetchPublicationsForCalendarRange';
import { collectCalendarLayoutDateKeys, getCalendarRange } from './calendar/functions';
import {
    buildCalendarDayLabels,
    buildCalendarPeriodLabel,
} from './calendar/calendarTemporalLabels';
import { CalendarGranularity, type CalendarPublication } from './calendar/types';
import { PublicationsCalendar } from './calendar/PublicationsCalendar';
import { PageLayout } from '../layout/PageLayout';
import { PublicationPageHeader } from './PublicationPageHeader';
import { SearchInput } from './form/SearchInput';
import { CalendarDays, LayoutList } from 'lucide-react';
import { DATE_FORMAT_ISO_DAY } from '../constants/dateFormat';
import { ButtonLink, IconButton } from '../components/Button';
import { buildPublicationListSearchParams } from './buildPublicationListSearchParams';
import {
    getActiveSocialNetworkFilters,
    publicationMatchesSocialNetworks,
    type SocialNetworkFilterKey,
} from './socialNetworkFilter';

interface Publication {
    media: string;
    extension: string;
    title: string;
    content: string;
    status: string;
    social?: string[] | string;
    createdAt: string;
    publicationDate: string;
    createdByAdmin: string;
    createdByUser: string;
    id: number;
}

enum PublicationsViewMode {
    Calendar = 'calendar',
    List = 'list',
}

const download = { path: downloadIcon };

const publicationCard = (params: any) => {
    return (
        <>
            <div className={'flex w-full items-center relative break-words'}>
                <img
                    src={mediaUrl(params.data)}
                    className={'w-[80px] h-[80px] p-[10px] rounded-[15px] flex'}
                    alt="illustration"
                />
                <div className={'flex flex-col w-[75%] overflow-hidden'}>
                    <div className={'overflow-hidden text-ellipsis w-full'}>
                        {params.data.isGlobal && (
                            <span>
                                <img
                                    className={'max-w-[80px] max-h-[20px]'}
                                    src={logoNF}
                                    alt="logo News Factory"
                                />
                            </span>
                        )}
                        <span
                            className={
                                'relative text-medium font-medium text-dark-gray my-[10px] w-full'
                            }
                        >
                            {params.data.title}
                        </span>
                    </div>
                    <div className={'overflow-hidden text-ellipsis w-full'}>
                        <span
                            className={
                                'relative text-medium font-small text-gray w-full whitespace-normal leading-[1.5em] line-clamp-2'
                            }
                        >
                            {params.data.content}
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

function Publications() {
    const userEmail = useSelector((state: RootState) => state.user.email);
    const userGroupement = useSelector((state: RootState) => state.user.groupement);
    const userId = useSelector((state: RootState) => state.user.id);
    const isAdmin = useSelector((state: RootState) => state.user.isAdmin);
    const isSsoUser = useSelector((state: RootState) => state.user.isSsoUser);
    const isAdminGrp = !isAdmin && !isSsoUser && userGroupement?.id !== 1;
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [publications, setPublications] = useState<Publication[]>([]);
    const [searchText, setSearchText] = useState('');
    const [searchStatus, setSearchStatus] = useState<string>('');
    const [searchNetwork, setSearchNetwork] = useState<Record<SocialNetworkFilterKey, boolean>>({
        facebook: false,
        instagram: false,
        linkedin: false,
        googleMyBusiness: false,
    });
    const [isModalOpen, setModalOpen] = useState(false);
    const [isDateOpen, setDateOpen] = useState(false);
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [dateOrder, setDateOrder] = useState<{ field: string; value: string }>({
        field: '',
        value: '',
    });
    const fetchRequestIdRef = useRef(0);
    const [isArchiveModalOpened, setIsArchivedModalOpened] = useState<boolean>(false);
    const [modalId, setModalId] = useState<number>(0);
    const [selectedPublications, setSelectedPublications] = useState<number[]>([]);
    const [allPublications, setAllPublications] = useState<Publication[]>([]);
    const [viewMode, setViewMode] = useState(PublicationsViewMode.List);
    const [calendarAnchor, setCalendarAnchor] = useState(() => new Date());
    const [calendarGranularity, setCalendarGranularity] = useState(CalendarGranularity.Month);
    const [calendarPublications, setCalendarPublications] = useState<
        readonly CalendarPublication[]
    >([]);
    const [calendarLoading, setCalendarLoading] = useState(false);

    let itemsPerPage = 10;
    const url = `/api/publications`;

    const handleCloseArchiveModal = () => {
        setIsArchivedModalOpened(false);
        setModalId(0);
    };

    async function fecthAllPublications(): Promise<Publication[]> {
        setLoading(true);
        let allPublications: Publication[] = [];
        let itemsPerPage = 500;
        let fetchPage = 1;
        let hasMore = true;

        while (hasMore) {
            const response = await axios.get(
                url +
                    `?itemsPerPage=${itemsPerPage}&page=${currentPage}&order%5BcreatedAt%5D=asc` +
                    (isAdminGrp ? `&groupement=${userGroupement?.id}` : ''),
            );

            allPublications = allPublications.concat(response.data['hydra:member']);

            const allItems = response.data['hydra:totalItems'];
            hasMore = fetchPage * itemsPerPage < allItems;
            fetchPage++;
            setCurrentPage(fetchPage);
        }

        setLoading(false);

        return allPublications;
    }

    async function handleSearchText(text: string) {
        setSearchText(text);
        setCurrentPage(1);
    }

    async function handleSearchStatus(status: string) {
        setCurrentPage(1);
        setSearchStatus(status);
    }

    const handleNetworkChange = (network: SocialNetworkFilterKey) => {
        setCurrentPage(1);
        setSearchNetwork(prev => ({
            ...prev,
            [network]: !prev[network],
        }));
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleOpenDate = () => {
        setDateOpen(true);
    };

    const handleStat = async (id: number) => {
        try {
            const response = await axios.get(`/api/publication_stat/${id}`);

            if (response.data.stat) {
                const filename = `publication_stats${id}.csv`;
                const headers = [
                    'publicationId',
                    'title',
                    'network',
                    'status',
                    'message',
                    'agencyName',
                ];
                exportToCsv(filename, headers, response.data.stat);
            }
        } catch (error) {
            console.error('Error when fetching publication stat : ', error);
        }
    };

    const handleArchive = async (id: number) => {
        const date = new Date();
        try {
            const data = {
                status: 0,
                updatedBy: userEmail,
                updatedAt: format(date, DATE_FORMAT_ISO_DAY),
            };
            const response = await axios.patch(`/api/publications/${id}`, data, {
                headers: {
                    'Content-Type': 'application/merge-patch+json',
                },
            });

            if (response.status === 200) {
                setPublications(prevPublications =>
                    prevPublications.filter(publication => publication.id !== id),
                );
                setIsArchivedModalOpened(false);
            }
        } catch (error) {
            console.error(`Error when archive publication ${id} : `, error);
        }
    };

    const handleOpenArchiveModal = useCallback((id: number) => {
        setIsArchivedModalOpened(true);
        setModalId(id);
    }, []);

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleCloseDate = () => {
        setDateOpen(false);
    };

    const handleSearchDate = (value: string) => {
        setCurrentPage(1);
        if (value === 'CASC' || value === 'CDESC' || value === 'PASC' || value === 'PDESC') {
            switch (value) {
                case 'CASC':
                    setDateOrder({
                        field: 'order',
                        value: 'asc',
                    });
                    break;
                case 'CDESC':
                    setDateOrder({
                        field: 'order',
                        value: 'desc',
                    });
                    break;
                case 'PASC':
                    setDateOrder({
                        field: 'pubDate',
                        value: 'asc',
                    });
                    break;
                case 'PDESC':
                    setDateOrder({
                        field: 'pubDate',
                        value: 'desc',
                    });
                    break;
            }
        } else {
            setDateOrder({ field: '', value: '' });
        }
    };

    const handleDateChange = (selection: any) => {
        setCurrentPage(1);
        if (selection) {
            setDateStart(format(selection.startDate, DATE_FORMAT_ISO_DAY));
            setDateEnd(format(selection.endDate, DATE_FORMAT_ISO_DAY));
        } else {
            setDateStart('');
            setDateEnd('');
        }
    };

    const handleResetFilters = () => {
        setCurrentPage(1);
        setSearchText('');
        setSearchStatus('');
        setSearchNetwork({
            facebook: false,
            instagram: false,
            linkedin: false,
            googleMyBusiness: false,
        });
        setModalOpen(false);
    };

    const handleResetDateFilters = () => {
        setCurrentPage(1);
        setDateStart('');
        setDateEnd('');
        setDateOrder({ field: '', value: '' });
        setDateOpen(false);
    };

    const headers = [
        'id',
        'title',
        'content',
        'media',
        'link',
        'social',
        'status',
        'createdAt',
        'date',
    ];

    const onExportClick = () => {
        handleExportClick(
            headers,
            fecthAllPublications,
            'publications.csv',
            () => setLoading(true),
            () => setLoading(false),
        );
    };

    useEffect(() => {
        const fetchSelectedPublications = async () => {
            try {
                const response = await axios.get(
                    `/api/user_unpublishes?user=${userId}&pagination=false`,
                );
                const selectedIds = response.data['hydra:member'].map((pub: any) => {
                    const parts = pub.publication.split('/');
                    return parseInt(parts[parts.length - 1], 10);
                });
                setSelectedPublications(selectedIds);
            } catch (error) {
                console.error(
                    'Erreur lors de la récupération des publications sélectionnées :',
                    error,
                );
            }
        };
        fetchSelectedPublications();
    }, [userId]);

    const togglePublicationSelection = async (id: number) => {
        try {
            if (selectedPublications.includes(id)) {
                const response = await axios.get(
                    `/api/user_unpublishes?user=${userId}&publication=${id}`,
                );
                const publicationToDelete = response.data['hydra:member'][0];
                if (publicationToDelete) {
                    await axios.delete(`/api/user_unpublishes/${publicationToDelete.id}`);
                    setSelectedPublications(prev => prev.filter(pubId => pubId !== id));
                }
            } else {
                await axios.post(
                    '/api/user_unpublishes',
                    {
                        user: `/api/users/${userId}`,
                        publication: `/api/publications/${id}`,
                    },
                    {
                        headers: { 'Content-Type': 'application/ld+json' },
                    },
                );
                setSelectedPublications(prev => [...prev, id]);
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la publication sélectionnée :', error);
        }
    };

    const updateSelectedPublications = useCallback(async () => {
        try {
            const response = await axios.get(
                `/api/user_unpublishes?user=${userId}&pagination=false`,
            );
            const selectedIds = response.data['hydra:member'].map((item: any) => {
                const parts = item.publication.split('/');
                return parseInt(parts[parts.length - 1], 10);
            });

            setSelectedPublications(selectedIds);
        } catch (error) {
            console.error('Erreur lors de la mise à jour des publications sélectionnées :', error);
        }
    }, [userId]);

    const fetchData = useCallback(async () => {
        if (searchText !== '' && searchText.length <= 3) {
            setAllPublications([]);
            return;
        }

        const requestId = ++fetchRequestIdRef.current;

        try {
            setLoading(true);
            let combinedPublications: Publication[] = [];
            let apiResponse: AxiosResponse<any, any>;
            const nextSearchParams = buildPublicationListSearchParams({
                searchText,
                searchStatus,
                searchNetwork,
                dateStart,
                dateEnd,
                dateOrder,
            });
            const adminUrl = `/api/publications-all-accessible-for-admin`;
            const userUrl = `/api/publications-all-accessible-for-user/` + userId;
            const publicationUrl = isSsoUser
                ? userUrl + nextSearchParams
                : adminUrl + nextSearchParams;

            if (isSsoUser) {
                const userResponse = await axios.get(publicationUrl);
                combinedPublications = combinedPublications.concat(userResponse.data);
            } else {
                apiResponse = await axios.get(publicationUrl);
                combinedPublications = apiResponse.data;
            }

            if (requestId !== fetchRequestIdRef.current) {
                return;
            }

            let uniquePublications = Array.from(
                new Map(combinedPublications.map(pub => [pub.id, pub])).values(),
            );

            const activeNetworks = getActiveSocialNetworkFilters(searchNetwork);
            if (activeNetworks.length > 0) {
                uniquePublications = uniquePublications.filter(publication =>
                    publicationMatchesSocialNetworks(publication, activeNetworks),
                );
            }

            setTotalPage(Math.ceil(uniquePublications.length / itemsPerPage));
            setAllPublications(uniquePublications);
        } catch (error) {
            console.error('Erreur lors de la récupération des publications :', error);
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [
        dateEnd,
        dateOrder,
        dateStart,
        isSsoUser,
        itemsPerPage,
        searchNetwork,
        searchStatus,
        searchText,
        userId,
    ]);

    useEffect(() => {
        if (viewMode !== PublicationsViewMode.Calendar) {
            return;
        }
        let cancelled = false;
        const run = async () => {
            if (!userId) {
                if (!cancelled) {
                    setCalendarPublications([]);
                    setCalendarLoading(false);
                }
                return;
            }
            try {
                setCalendarLoading(true);
                const { from, to } = getCalendarRange(calendarAnchor, calendarGranularity);
                const fromStr = format(from, DATE_FORMAT_ISO_DAY);
                const toStr = format(to, DATE_FORMAT_ISO_DAY);
                const calendarStatusFilter: '1' | '2' | '-1' | undefined =
                    searchStatus === '1' || searchStatus === '2' || searchStatus === '-1'
                        ? searchStatus
                        : undefined;
                const calendarSocialFilters = getActiveSocialNetworkFilters(searchNetwork);
                const rows = await fetchPublicationsForCalendarRange({
                    isSsoUser,
                    userId,
                    fromDate: fromStr,
                    toDate: toStr,
                    titleQuery: searchText.length >= 3 ? searchText : undefined,
                    status: calendarStatusFilter,
                    socialNetworks:
                        calendarSocialFilters.length === 1 ? calendarSocialFilters : undefined,
                });
                const filteredRows =
                    calendarSocialFilters.length > 0
                        ? rows.filter(publication =>
                              publicationMatchesSocialNetworks(publication, calendarSocialFilters),
                          )
                        : rows;
                if (!cancelled) {
                    setCalendarPublications(filteredRows);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du calendrier :', error);
                if (!cancelled) {
                    setCalendarPublications([]);
                }
            } finally {
                if (!cancelled) {
                    setCalendarLoading(false);
                }
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [
        viewMode,
        calendarAnchor,
        calendarGranularity,
        isSsoUser,
        userId,
        searchText,
        searchStatus,
        searchNetwork,
    ]);

    const calendarDayLabels = useMemo(() => {
        if (viewMode !== PublicationsViewMode.Calendar || !userId) {
            return {};
        }
        return buildCalendarDayLabels(
            collectCalendarLayoutDateKeys(calendarAnchor, calendarGranularity),
        );
    }, [viewMode, userId, calendarAnchor, calendarGranularity]);

    const calendarPeriodLabel = useMemo(() => {
        if (
            viewMode !== PublicationsViewMode.Calendar ||
            !userId ||
            calendarGranularity === CalendarGranularity.Month
        ) {
            return '';
        }
        return buildCalendarPeriodLabel(calendarAnchor, calendarGranularity);
    }, [viewMode, userId, calendarAnchor, calendarGranularity]);

    useEffect(() => {
        if (viewMode === PublicationsViewMode.List) {
            void fetchData();
        }
    }, [
        viewMode,
        fetchData,
        currentPage,
        searchText,
        searchStatus,
        searchNetwork,
        dateStart,
        dateEnd,
        dateOrder,
    ]);

    useEffect(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setPublications(allPublications.slice(startIndex, endIndex));
    }, [allPublications, currentPage, itemsPerPage]);

    function dateGenerator(dateAsString: any) {
        let newDate = new Date(dateAsString);
        let day_month = newDate.toLocaleString('fr-FR', {
            day: 'numeric',
            month: 'short',
        });
        let year = newDate.toLocaleString('fr-FR', { year: 'numeric' });
        return (
            <>
                <div className="flex items-center h-full w-full flex-col justify-center leading-normal">
                    <span className="text-base font-semibold">{day_month}</span>
                    <span className="text-sm font-normal">{year}</span>
                </div>
            </>
        );
    }

    function dateFormatter(params: any) {
        return dateGenerator(params.data.publicationDate);
    }

    function createdAtFormatter(params: any) {
        return dateGenerator(params.data.createdAt);
    }

    function actionButtons(params: any) {
        let id = params.data.id;
        let status = params.data.status;
        let isGlobal = params.data.isGlobal;
        let createdByAdminUrl = params.data.createdByAdmin;
        let createdByUserUrl = params.data.createdByUser;
        const extractIdFromUrl = (url: string) => {
            if (!url) return null;
            const parts = url.split('/');
            return parts.length > 0 ? parseInt(parts[parts.length - 1]) : null;
        };
        const createdByAdminId = extractIdFromUrl(createdByAdminUrl);
        const createdByUserId = extractIdFromUrl(createdByUserUrl);

        const isCreatedByAdmin = createdByAdminId === userId;
        const isCreatedByUser = createdByUserId === userId;
        const isSelected = !selectedPublications.includes(id);

        return (
            <>
                {isGlobal ? (
                    <div className="flex items-center justify-center h-full w-full gap-3">
                        {status === 2 ? (
                            <>
                                {isCreatedByAdmin ? (
                                    <>
                                        <Link to={`/show_publication/${id}`}>
                                            <img
                                                src={visibility}
                                                className={'w-[20px] h-[20px]'}
                                                alt="visibility icon"
                                            />
                                        </Link>
                                        <button onClick={() => handleStat(id)} className={'p-0'}>
                                            <img
                                                src={stat}
                                                className={'w-[20px] h-[20px]'}
                                                alt="stat icon"
                                            />
                                        </button>
                                        <button
                                            onClick={() => handleOpenArchiveModal(id)}
                                            className={'p-0'}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <Link to={`/show_publication/${id}`}>
                                        <img
                                            src={visibility}
                                            className={'w-[20px] h-[20px]'}
                                            alt="visibility icon"
                                        />
                                    </Link>
                                )}
                            </>
                        ) : (
                            <>
                                {isCreatedByAdmin ? (
                                    <>
                                        <Link to={`/edit_publication/${id}`}>
                                            <img
                                                src={editOn}
                                                className={'w-[20px] h-[20px]'}
                                                alt="edit icon"
                                            />
                                        </Link>
                                        <button
                                            onClick={() => handleOpenArchiveModal(id)}
                                            className={'p-0'}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <div className="relative inline-flex items-center gap-2">
                                        <Link to={`/show_publication/${id}`}>
                                            <img
                                                src={visibility}
                                                className={'w-[20px] h-[20px]'}
                                                alt="visibility icon"
                                            />
                                        </Link>
                                        {isSsoUser && (
                                            <button
                                                type={'button'}
                                                onClick={() => togglePublicationSelection(id)}
                                                className={
                                                    'p-0 relative bg-transparent border-0 cursor-pointer'
                                                }
                                                title={
                                                    isSelected
                                                        ? 'Déprogrammer la publication'
                                                        : 'Programmer la publication'
                                                }
                                            >
                                                <img
                                                    src={isSelected ? progPubli : unselected}
                                                    className={'w-[20px] h-[20px]'}
                                                    alt="toggle publi icon"
                                                />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full w-full gap-3">
                        {status === 2 ? (
                            <>
                                {isCreatedByAdmin || isCreatedByUser ? (
                                    <>
                                        <button
                                            className={'p-0'}
                                            onClick={() => handleOpenArchiveModal(id)}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <img
                                        src={visibility}
                                        className={'w-[20px] h-[20px]'}
                                        alt="visibility icon"
                                        style={{ opacity: 0.5 }}
                                    />
                                )}
                                {(isCreatedByAdmin || isCreatedByUser) && (
                                    <Link to={`/show_publication/${id}`}>
                                        <img
                                            src={visibility}
                                            className={'w-[20px] h-[20px]'}
                                            alt="visibility icon"
                                        />
                                    </Link>
                                )}
                            </>
                        ) : (
                            <>
                                {isCreatedByAdmin || isCreatedByUser ? (
                                    <>
                                        <Link to={`/edit_publication/${id}`}>
                                            <img
                                                src={editOn}
                                                className={'w-[20px] h-[20px]'}
                                                alt="edit icon"
                                            />
                                        </Link>
                                        <button
                                            className={'p-0'}
                                            onClick={() => handleOpenArchiveModal(id)}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <Link to={`/show_publication/${id}`}>
                                        <img
                                            src={visibility}
                                            className={'w-[20px] h-[20px]'}
                                            alt="visibility icon"
                                        />
                                    </Link>
                                )}
                            </>
                        )}
                    </div>
                )}
            </>
        );
    }

    const defaultColDef = useMemo(() => {
        return {
            resizable: false,
            sortable: false,
            suppressMovable: true,
        };
    }, []);

    const columnDefs: ColDef<Publication>[] = [
        {
            headerName: 'Publication',
            cellRenderer: publicationCard,
            autoHeight: true,
            flex: 3,
        },
        { headerName: 'Status', cellRenderer: statusCellRenderer, flex: 1 },
        {
            headerName: 'Créée Le',
            field: 'createdAt',
            sortable: true,
            cellRenderer: createdAtFormatter,
            flex: 1,
        },
        {
            headerName: 'Publiée Le',
            field: 'publicationDate',
            sortable: true,
            cellRenderer: dateFormatter,
            flex: 1,
        },
        {
            headerName: 'Réseaux Sociaux',
            cellRenderer: socialMediaCellRenderer,
            flex: 1,
        },
        { headerName: 'Action', cellRenderer: actionButtons, flex: 1 },
    ];

    const containerStyle = useMemo(
        () => ({
            width: '100%',
            height: '-webkit-fill-available',
            fontFamily: 'Montserrat',
        }),
        [],
    );

    return (
        <>
            <PageLayout>
                <PublicationPageHeader
                    title="Mes publications"
                    actions={
                        <>
                            <ButtonLink to={'/archived_publications'} isPrimary={false}>
                                Mes publications archivées
                            </ButtonLink>
                            <AddPublicationButton />
                        </>
                    }
                />
                {(isSsoUser || isAdminGrp) &&
                    (() => {
                        const hasSpecificGroupement = userGroupement && userGroupement.id !== 1;
                        const showLogoSwitch = !hasSpecificGroupement || isAdminGrp;
                        const showBothSwitches = isSsoUser && showLogoSwitch;

                        return (
                            <div
                                style={{
                                    alignSelf: 'stretch',
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start',
                                    gap: 24,
                                    display: 'flex',
                                    marginBottom: 24,
                                    width: '100%',
                                }}
                            >
                                {isSsoUser && (
                                    <AutoPubSwitch
                                        updateSelectedPublications={updateSelectedPublications}
                                        newStyle={showBothSwitches}
                                    />
                                )}
                                <LogoSwitch newStyle={showBothSwitches} />
                            </div>
                        );
                    })()}

                <div className="flex w-full flex-wrap items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                        <SearchInput
                            search={searchText}
                            handleSearch={handleSearchText}
                            placeholder="Rechercher une publication..."
                        />
                        <div className="flex items-center gap-3">
                            <div className="relative shrink-0 self-start">
                                <IconButton
                                    isPrimary={false}
                                    className={'rounded-md'}
                                    label="Trier par"
                                    icon={sortIcon}
                                    onClick={handleOpenModal}
                                />
                                <Filter isOpen={isModalOpen} onClose={handleCloseModal}>
                                    <div className="flex flex-row justify-between w-full border-b border-light-gray pb-[12px]">
                                        <span className="text-super-medium font-medium text-dark-gray">
                                            Trier par
                                        </span>
                                        <Link
                                            className="text-medium font-medium text-primary mt-1"
                                            onClick={() => handleResetFilters()}
                                            to=""
                                        >
                                            Réinitialiser
                                        </Link>
                                    </div>
                                    <div className="flex w-full flex-col border-b border-light-gray pb-3">
                                        <span className="flex w-full text-medium font-medium pb-3">
                                            Status
                                        </span>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                id="status-en-cours"
                                                type="radio"
                                                value={'1'}
                                                name={'status'}
                                                onChange={e => handleSearchStatus(e.target.value)}
                                            />
                                            <label
                                                htmlFor="status-en-cours"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoInProgressColored}
                                                    alt="en cours"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Status en cours
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                id="status-terminee"
                                                type="radio"
                                                value={'2'}
                                                name={'status'}
                                                onChange={e => handleSearchStatus(e.target.value)}
                                            />
                                            <label
                                                htmlFor="status-terminee"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoFinishedColored}
                                                    alt="terminé"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Status terminée
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                id="status-brouillon"
                                                type="radio"
                                                value={'-1'}
                                                name={'status'}
                                                onChange={e => handleSearchStatus(e.target.value)}
                                            />
                                            <label
                                                htmlFor="status-brouillon"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoDraftColored}
                                                    alt="brouillon"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Brouillon
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex w-full flex-col">
                                        <span className="flex w-full text-medium font-medium pb-3">
                                            Réseaux sociaux
                                        </span>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'facebook'}
                                                value={'facebook'}
                                                checked={searchNetwork.facebook}
                                                onChange={() => handleNetworkChange('facebook')}
                                            />
                                            <label
                                                htmlFor="facebook"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoFbColored}
                                                    alt="Facebook"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Facebook
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'instagram'}
                                                value={'instagram'}
                                                checked={searchNetwork.instagram}
                                                onChange={() => handleNetworkChange('instagram')}
                                            />
                                            <label
                                                htmlFor="instagram"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoInstaColored}
                                                    alt="Instagram"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Instagram
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'linkedin'}
                                                value={'linkedin'}
                                                checked={searchNetwork.linkedin}
                                                onChange={() => handleNetworkChange('linkedin')}
                                            />
                                            <label
                                                htmlFor="linkedin"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoInColored}
                                                    alt="linkedin"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Linkedin
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'googleMyBusiness'}
                                                value={'googleMyBusiness'}
                                                checked={searchNetwork.googleMyBusiness}
                                                onChange={() =>
                                                    handleNetworkChange('googleMyBusiness')
                                                }
                                            />
                                            <label
                                                htmlFor="gmb"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoGmbColored}
                                                    alt="google my business"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Google My Business
                                            </label>
                                        </div>
                                    </div>
                                </Filter>
                            </div>
                            {viewMode === PublicationsViewMode.List ? (
                                <div className="relative shrink-0 self-start">
                                    <IconButton
                                        label="Date"
                                        icon={dateIcon}
                                        isPrimary={false}
                                        className={'rounded-md'}
                                        onClick={handleOpenDate}
                                    />
                                    <Filter isOpen={isDateOpen} onClose={handleCloseDate}>
                                        <Link
                                            className="text-primary ml-[70%] mt-[10px]"
                                            onClick={() => handleResetDateFilters()}
                                            to=""
                                        >
                                            Réinitialiser
                                        </Link>
                                        <span>Date</span>
                                        <div>
                                            <p>Création / Publication</p>
                                            <div className="flex w-full items-center content-center">
                                                <input
                                                    type="radio"
                                                    value={'CASC'}
                                                    name={'dateOrder'}
                                                    onChange={e => handleSearchDate(e.target.value)}
                                                />
                                                <label
                                                    className="flex m-0 text-medium text-gray"
                                                    htmlFor="Date de création croissante"
                                                >
                                                    Date de création croissante
                                                </label>
                                            </div>
                                            <div className="flex w-full items-center content-center">
                                                <input
                                                    type="radio"
                                                    value={'CDESC'}
                                                    name={'dateOrder'}
                                                    onChange={e => handleSearchDate(e.target.value)}
                                                />
                                                <label
                                                    className="flex m-0 text-medium text-gray"
                                                    htmlFor="terminee"
                                                >
                                                    Date de création décroissante
                                                </label>
                                            </div>
                                            <div className="flex w-full items-center content-center">
                                                <input
                                                    type="radio"
                                                    value={'PASC'}
                                                    name={'dateOrder'}
                                                    onChange={e => handleSearchDate(e.target.value)}
                                                />
                                                <label
                                                    className="flex m-0 text-medium text-gray"
                                                    htmlFor="Date de création croissante"
                                                >
                                                    Date de publication croissante
                                                </label>
                                            </div>
                                            <div className="flex w-full items-center content-center">
                                                <input
                                                    type="radio"
                                                    value={'PDESC'}
                                                    name={'dateOrder'}
                                                    onChange={e => handleSearchDate(e.target.value)}
                                                />
                                                <label
                                                    className="flex m-0 text-medium text-gray"
                                                    htmlFor="terminee"
                                                >
                                                    Date de publication décroissante
                                                </label>
                                            </div>
                                        </div>
                                        <div className={'relative w-fit'}>
                                            <p>Par période</p>
                                            <div className={'z-[1000]'}>
                                                <Link
                                                    className="text-primary ml-[68%] mt-[10px]"
                                                    onClick={() => handleResetDateFilters()}
                                                    to=""
                                                >
                                                    Réinitialiser
                                                </Link>
                                                <DateRangeComponent
                                                    onDateChange={handleDateChange}
                                                />
                                            </div>
                                        </div>
                                    </Filter>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-3">
                        {!isSsoUser && viewMode === PublicationsViewMode.List && (
                            <button
                                className="flex items-center text-dark-gray text-medium font-medium bg-primary-light"
                                onClick={onExportClick}
                                disabled={loading}
                            >
                                <img
                                    src={download.path}
                                    alt="download icon"
                                    className="mr-2 w-8 h-8"
                                />
                                Exporter
                            </button>
                        )}
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                aria-pressed={viewMode === PublicationsViewMode.List}
                                onClick={() => setViewMode(PublicationsViewMode.List)}
                                className={
                                    'flex items-center gap-1.5 rounded-lg px-3 py-2 text-medium font-medium ' +
                                    (viewMode === PublicationsViewMode.List
                                        ? 'bg-white text-dark-gray shadow-sm ring-1 ring-light-gray'
                                        : 'bg-primary-light text-gray')
                                }
                            >
                                <LayoutList className="h-5 w-5 shrink-0" aria-hidden />
                            </button>
                            <button
                                type="button"
                                aria-pressed={viewMode === PublicationsViewMode.Calendar}
                                onClick={() => {
                                    setDateOpen(false);
                                    setViewMode(PublicationsViewMode.Calendar);
                                }}
                                className={
                                    'flex items-center gap-1.5 rounded-lg px-3 py-2 text-medium font-medium ' +
                                    (viewMode === PublicationsViewMode.Calendar
                                        ? 'bg-white text-dark-gray shadow-sm ring-1 ring-light-gray'
                                        : 'bg-primary-light text-gray')
                                }
                            >
                                <CalendarDays className="h-5 w-5 shrink-0" aria-hidden />
                            </button>
                        </div>
                    </div>
                </div>
                {viewMode === PublicationsViewMode.List ? (
                    <div style={containerStyle}>
                        <div className="ag-theme-alpine relative" style={containerStyle}>
                            <Spinner loading={loading} />
                            <AgGridReact
                                rowData={publications}
                                defaultColDef={defaultColDef}
                                columnDefs={columnDefs}
                                domLayout="autoHeight"
                                pagination={false}
                                overlayNoRowsTemplate={'Pas de données enregistrées'}
                                suppressNoRowsOverlay={!!publications}
                            />
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPage={totalPage}
                            previous={() => setCurrentPage(p => p - 1)}
                            next={() => setCurrentPage(p => p + 1)}
                        />
                    </div>
                ) : (
                    <div className="relative w-full min-h-[420px] bg-white p-3 rounded-md">
                        <div
                            aria-busy={calendarLoading}
                            className={
                                'transition-opacity ' +
                                (calendarLoading
                                    ? 'pointer-events-none select-none opacity-50 grayscale'
                                    : '')
                            }
                        >
                            <PublicationsCalendar
                                anchorDate={calendarAnchor}
                                onAnchorChange={setCalendarAnchor}
                                granularity={calendarGranularity}
                                onGranularityChange={setCalendarGranularity}
                                publications={calendarPublications}
                                periodLabel={calendarPeriodLabel}
                                dayLabels={calendarDayLabels}
                            />
                        </div>
                        {calendarLoading && (
                            <div aria-hidden className="absolute inset-0 z-40 bg-light-gray/40" />
                        )}
                        <Spinner loading={calendarLoading} />
                    </div>
                )}
            </PageLayout>
            <Modal
                isOpen={isArchiveModalOpened}
                onClose={handleCloseArchiveModal}
                className={'flex flex-col justify-between w-full min-h-48'}
            >
                <div
                    className={
                        'flex flex-col justify-between items-center w-full h-full flex-1 text-center'
                    }
                >
                    <span className="text-heavy font-super-heavy">
                        Etes-vous sur de vouloir archiver <br />
                        cette publication ?
                    </span>
                    <span className="my-8">Cette publication ne sera pas publiée.</span>
                    <div className={'flex justify-between items-center w-full h-full'}>
                        <button
                            className={
                                'px-4 py-2 leading-[1.5] rounded-[1.5rem] flex justify-center items-center w-[200px] text-small font-medium mb-2.5 bg-primary border border-light-gray'
                            }
                            onClick={() => handleArchive(modalId)}
                        >
                            Oui
                        </button>
                        <button
                            className={
                                'px-4 py-2 leading-[1.5] rounded-[1.5rem] flex justify-center items-center w-[200px] text-small font-medium mb-2.5 bg-white border border-light-gray'
                            }
                            onClick={handleCloseArchiveModal}
                        >
                            Non
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default Publications;
